import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

export const remitterAccountViewAll = createAsyncThunk(
  "remitterAccountViewAll",
  async (params, { rejectWithValue }) => {
    const {
      page_no,
      page_size,
      search_string,
      start_date,
      end_date,
      labels,
      approval_status,
      sort_by,
    } = params;
    try {
      const res = await axiosInstance.get(`${baseUrl}/accounts/remitter`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          page_no,
          page_size,
          search_string,
          start_date,
          end_date,
          labels,
          approval_status: [approval_status],
          sort_by,
        },
      });
      return res.data;
    } catch (error) {
      const msg = error?.response?.data?.errors?.base?.[0];
      CommonToast({
        text: msg,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(msg);
    }
  },
);

export const remitterAccountDetailsById = createAsyncThunk(
  "remitterAccountDetailsById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(
        `${baseUrl}/accounts/remitter/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const remitterAccountCreate = createAsyncThunk(
  "remitterAccountCreate",
  async ({ name, labels }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `${baseUrl}/accounts/remitter`,
        {
          remitter_accounts: { name, labels },
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      if (!res.data?.status) {
        const msg = res.data?.errors?.name?.[0] || res.data?.errors?.base?.[0];

        return rejectWithValue(msg);
      }

      return res.data;
    } catch (error) {
      const msg =
        error?.response?.data?.errors?.name?.[0] ||
        error?.response?.data?.errors?.base?.[0] ||
        error?.message;

      return rejectWithValue(msg);
    }
  },
);

export const remitterAccountApprove = createAsyncThunk(
  "remitterAccountApprove",
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/approve`,
        {},
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const remitterAccountReject = createAsyncThunk(
  "remitterAccountReject",
  async ({ id, rejection_reason }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/reject`,
        { rejection_reason: rejection_reason },
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      if (!res.data?.status) {
        return rejectWithValue(res.data);
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const remitterAccountArchive = createAsyncThunk(
  "remitterAccountArchive",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/archive`,
        {},
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const remitterAccountUnarchive = createAsyncThunk(
  "remitterAccountUnarchive",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/unarchive`,
        {},
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const remitterAccountSlice = createSlice({
  name: "remitterAccount",
  initialState: {
    remitterAccountViewAllData: null,
    remitterAccountViewAllLoading: false,
    remitterAccountViewAllError: null,

    remitterAccountCreateData: null,
    remitterAccountCreateLoading: false,
    remitterAccountCreateError: null,

    remitterAccountDetailsData: null,
    remitterAccountDetailsLoading: false,
    remitterAccountDetailsError: null,

    remitterAccountApproveData: null,
    remitterAccountApproveLoading: false,
    remitterAccountApproveError: null,

    remitterAccountRejectData: null,
    remitterAccountRejectLoading: false,
    remitterAccountRejectError: null,

    remitterAccountArchiveData: null,
    remitterAccountArchiveLoading: false,
    remitterAccountArchiveError: null,

    remitterAccountUnarchiveData: null,
    remitterAccountUnarchiveLoading: false,
    remitterAccountUnarchiveError: null,
  },

  reducers: {
    resetRemitterDetails: (state) => {
      state.remitterDetails = null;
    },
    resetCreateRemitter(state) {
      state.remitterAccountCreateData = null;
      state.remitterAccountCreateError = null;
    },
    setRemitterAccountDetailsData: (state, action) => {
      state.remitterAccountDetailsData = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(remitterAccountViewAll.pending, (state) => {
      state.remitterAccountViewAllLoading = true;
      state.remitterAccountViewAllError = null;
    });

    builder.addCase(remitterAccountViewAll.fulfilled, (state, action) => {
      state.remitterAccountViewAllLoading = false;
      state.remitterAccountViewAllData = action.payload;
    });

    builder.addCase(remitterAccountViewAll.rejected, (state, action) => {
      state.remitterAccountViewAllLoading = false;
      state.remitterAccountViewAllError = action.payload;
    });

    builder.addCase(remitterAccountCreate.pending, (state) => {
      state.remitterAccountCreateLoading = true;
    });

    builder.addCase(remitterAccountCreate.fulfilled, (state, action) => {
      state.remitterAccountCreateLoading = false;
      state.remitterAccountCreateData = action.payload;
    });

    builder.addCase(remitterAccountCreate.rejected, (state) => {
      state.remitterAccountCreateError = true;
      state.remitterAccountCreateData = null;
      state.remitterAccountCreateLoading = false;
    });

    builder.addCase(remitterAccountDetailsById.pending, (state) => {
      state.remitterAccountDetailsLoading = true;
    });
    builder.addCase(remitterAccountDetailsById.fulfilled, (state, action) => {
      state.remitterAccountDetailsLoading = false;
      state.remitterAccountDetailsData =
        action.payload?.remitter_accounts?.[0] || null;
    });
    builder.addCase(remitterAccountDetailsById.rejected, (state, action) => {
      state.remitterAccountDetailsLoading = false;
      state.remitterAccountDetailsError = action.payload;
    });

    builder.addCase(remitterAccountApprove.pending, (state) => {
      state.remitterAccountApproveLoading = true;
    });

    builder.addCase(remitterAccountApprove.fulfilled, (state, action) => {
      state.remitterAccountApproveLoading = false;
      state.remitterAccountApproveData = action.payload;
    });

    builder.addCase(remitterAccountApprove.rejected, (state) => {
      state.remitterAccountApproveError = true;
      state.remitterAccountApproveData = null;
      state.remitterAccountApproveLoading = false;
    });

    builder.addCase(remitterAccountReject.pending, (state) => {
      state.remitterAccountRejectLoading = true;
    });

    builder.addCase(remitterAccountReject.fulfilled, (state, action) => {
      state.remitterAccountRejectLoading = false;
      state.remitterAccountRejectData = action.payload;
    });

    builder.addCase(remitterAccountReject.rejected, (state) => {
      state.remitterAccountRejectError = true;
      state.remitterAccountRejectData = null;
      state.remitterAccountRejectLoading = false;
    });

    builder.addCase(remitterAccountArchive.pending, (state) => {
      state.remitterAccountArchiveLoading = true;
    });

    builder.addCase(remitterAccountArchive.fulfilled, (state, action) => {
      state.remitterAccountArchiveLoading = false;
      state.remitterAccountArchiveData = action.payload;
    });

    builder.addCase(remitterAccountArchive.rejected, (state) => {
      state.remitterAccountArchiveError = true;
      state.remitterAccountArchiveData = null;
      state.remitterAccountArchiveLoading = false;
    });

    builder.addCase(remitterAccountUnarchive.pending, (state) => {
      state.remitterAccountUnarchiveLoading = true;
    });

    builder.addCase(remitterAccountUnarchive.fulfilled, (state, action) => {
      state.remitterAccountUnarchiveLoading = false;
      state.remitterAccountUnarchiveData = action.payload;
    });

    builder.addCase(remitterAccountUnarchive.rejected, (state) => {
      state.remitterAccountUnarchiveError = true;
      state.remitterAccountUnarchiveData = null;
      state.remitterAccountUnarchiveLoading = false;
    });
  },
});

export const {
  resetRemitterDetails,
  resetCreateRemitter,
  setRemitterAccountDetailsData,
} = remitterAccountSlice.actions;
export default remitterAccountSlice.reducer;
